import { observable, freeze } from 'rewire-core';
import { IToggleMenuItem } from 'rewire-ui';
import { ReportOrientation, ReportOutput, ReportPaperSize } from '../DataTypes';
import { reportRouteManager } from '../main-navigation/RouteManager';

interface IReportFormatToggleOptions {
  pdf: boolean;
  excel: boolean;
  portrait: boolean;
  landscape: boolean;
  letter: boolean;
  legal: boolean;
  tabloid: boolean;
  extraWide: boolean;
  superWide: boolean;
}

interface IReportFormatsState {
  formatOptions: IReportFormatToggleOptions;
  items: IToggleMenuItem[];
}

class ReportFormatsModel {
  state: IReportFormatsState;

  constructor() {
    this.state = observable({
      formatOptions: {
        pdf: false,
        excel: false,
        portrait: false,
        landscape: false,
        letter: false,
        legal: false,
        tabloid: false,
        extraWide: false,
        superWide: false,
      },
      items: [
        {
          name: 'pdf',
          title: 'PDF',
          subheader: 'Output',
          active: () => this.state.formatOptions.pdf,
          disabled: () => false,
          onClick: this.handlePdfToggle,
        },
        {
          name: 'excel',
          title: 'Excel',
          active: () => this.state.formatOptions.excel,
          disabled: () => false,
          onClick: this.handleExcelToggle,
        },
        {
          name: 'portrait',
          title: 'Portrait',
          subheader: 'Orientation',
          active: () => this.state.formatOptions.portrait,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handlePortraitToggle,
        },
        {
          name: 'landscape',
          title: 'Landscape',
          active: () => this.state.formatOptions.landscape,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handleLandscapeToggle,
        },
        {
          name: 'letter',
          title: 'Letter',
          subheader: 'Paper Size',
          active: () => this.state.formatOptions.letter,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handleLetterToggle,
        },
        {
          name: 'legal',
          title: 'Legal',
          active: () => this.state.formatOptions.legal,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handleLegalToggle,
        },
        {
          name: 'tabloid',
          title: 'Tabloid',
          active: () => this.state.formatOptions.tabloid,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handleTabloidToggle,
        },
        {
          name: 'extraWide',
          title: 'Extra Wide',
          active: () => this.state.formatOptions.extraWide,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handleExtraWideToggle,
        },
        {
          name: 'superWide',
          title: 'Super Wide',
          active: () => this.state.formatOptions.superWide,
          disabled: () => this.state.formatOptions.excel,
          onClick: this.handleSuperWideToggle,
        },
      ],
    });
  }

  get items(): IToggleMenuItem[] {
    return this.state.items;
  }

  get formatOptions(): IReportFormatToggleOptions {
    return this.state.formatOptions;
  }

  get selectedOutput(): ReportOutput | undefined {
    if (this.formatOptions.pdf) return 'PDF';
    if (this.formatOptions.excel) return 'Excel';
    return undefined;
  }

  get selectedOrientation(): ReportOrientation | undefined {
    if (this.formatOptions.portrait) return 'Portrait';
    if (this.formatOptions.landscape) return 'Landscape';
    return undefined;
  }

  get selectedPaperSize(): ReportPaperSize | undefined {
    if (this.formatOptions.letter) return 'Letter';
    if (this.formatOptions.legal) return 'Legal';
    if (this.formatOptions.tabloid) return 'Tabloid';
    if (this.formatOptions.extraWide) return 'ExtraWide';
    if (this.formatOptions.superWide) return 'SuperWide';
    return undefined;
  }

  handlePdfToggle = () => {
    this.state.formatOptions.pdf = !this.state.formatOptions.pdf;
    this.state.formatOptions.excel = false;
    this.apply();
  };

  handleExcelToggle = () => {
    this.state.formatOptions.excel = !this.state.formatOptions.excel;
    this.state.formatOptions.pdf = false;
    if (this.state.formatOptions.excel) {
      // all other settings don't apply when creating an excel file
      this.state.formatOptions.portrait = false;
      this.state.formatOptions.landscape = false;
      this.state.formatOptions.letter = false;
      this.state.formatOptions.legal = false;
      this.state.formatOptions.tabloid = false;
      this.state.formatOptions.extraWide = false;
      this.state.formatOptions.superWide = false;
    }
    this.apply();
  };

  handlePortraitToggle = () => {
    this.state.formatOptions.portrait = !this.state.formatOptions.portrait;
    this.state.formatOptions.landscape = false;
    this.apply();
  };

  handleLandscapeToggle = () => {
    this.state.formatOptions.landscape = !this.state.formatOptions.landscape;
    this.state.formatOptions.portrait = false;
    this.apply();
  };

  handleSizeToggle = (size: ReportPaperSize, state: boolean) => {
    this.state.formatOptions.letter = size === 'Letter' && state;
    this.state.formatOptions.legal = size === 'Legal' && state;
    this.state.formatOptions.tabloid = size === 'Tabloid' && state;
    this.state.formatOptions.extraWide = size === 'ExtraWide' && state;
    this.state.formatOptions.superWide = size === 'SuperWide' && state;
    this.apply();
  };

  handleLetterToggle = () => this.handleSizeToggle('Letter', !this.state.formatOptions.letter);
  handleLegalToggle = () => this.handleSizeToggle('Legal', !this.state.formatOptions.legal);
  handleTabloidToggle = () => this.handleSizeToggle('Tabloid', !this.state.formatOptions.tabloid);
  handleExtraWideToggle = () => this.handleSizeToggle('ExtraWide', !this.state.formatOptions.extraWide);
  handleSuperWideToggle = () => this.handleSizeToggle('SuperWide', !this.state.formatOptions.superWide);

  reset() {
    freeze(() => {
      this.state.formatOptions.pdf = false;
      this.state.formatOptions.excel = false;
      this.state.formatOptions.portrait = false;
      this.state.formatOptions.landscape = false;
      this.state.formatOptions.letter = false;
      this.state.formatOptions.legal = false;
      this.state.formatOptions.tabloid = false;
      this.state.formatOptions.extraWide = false;
      this.state.formatOptions.superWide = false;
    });
    this.apply();
  }

  load() {
    // TODO: read from localStorage and set options for currently selected report?
    //       in reportModel, update localStorage when report is run to save the current selection
    const formats = reportRouteManager.formats ? { ...reportRouteManager.formats } : undefined;
    if (formats) {
      this.state.formatOptions = formats;
    }
  }

  apply = () => {
    setTimeout(() => {
      reportRouteManager.navigateTo({ formats: this.state.formatOptions });
    }, 0);
  };
}

const reportFormatsModel = new ReportFormatsModel();
export default reportFormatsModel;
